import React, { useState } from 'react';
import {ChevronDown, CreditCard, Calendar, Clock, Syringe, FileText, Shield, Users, HelpCircle, DollarSign, CheckCircle, MapPin} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
  icon: JSX.Element;
}

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqData: FAQItem[] = [
    {
      question: "What is the cost of a travel health consultation?",
      icon: <DollarSign className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>The fee is <strong>$60 per person, per consultation</strong>.</p>
        </div>
      )
    },
    {
      question: "What's included in the consultation?",
      icon: <CheckCircle className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>Your consultation includes personalized, pharmacist-led expert advice based on your individual itinerary, including:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Recommended and required travel vaccines (e.g., Hepatitis A, Yellow Fever, Dukoral)</li>
            <li>Malaria prevention recommendations</li>
            <li>Emergency antibiotics for traveller's diarrhea (when appropriate)</li>
            <li>Altitude illness prevention guidance</li>
            <li>Destination-specific health risks and prevention strategies</li>
          </ul>
        </div>
      )
    },
    {
      question: "Can I get my recommended vaccinations and prescriptions anywhere?",
      icon: <MapPin className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p><strong>Unfortunately, no.</strong> At this time, vaccinations and prescriptions can only be arranged and administered at our current Haven Travel Health partner locations. <a href="/contact" className="text-cyan-600 dark:text-cyan-400 font-semibold hover:underline">View all partner pharmacy locations →</a></p>
        </div>
      )
    },
    {
      question: "Where will I receive my recommended vaccines and prescriptions?",
      icon: <MapPin className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>After your virtual travel health consultation, we will schedule your vaccines and prescriptions at one of our Haven Travel Health partner clinics or pharmacies, based on availability and proximity to you.</p>
        </div>
      )
    },
    {
      question: "How can I pay for my booking?",
      icon: <CreditCard className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>Haven Travel Health uses <strong>Jane App</strong>, which securely stores your credit card information.</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Your credit card details are only visible to you and are not charged prior to your appointment.</li>
            <li>A valid credit card is required on file to confirm your consultation.</li>
            <li>Payment is processed after the appointment, and you will receive an email receipt.</li>
          </ul>
        </div>
      )
    },
    {
      question: "What is your cancellation policy?",
      icon: <Calendar className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <ul className="list-disc pl-5 space-y-2">
            <li>Cancellations must be made <strong>more than 24 hours</strong> before your scheduled appointment.</li>
            <li>Appointments cancelled within 24 hours of the scheduled time are subject to a cancellation fee equal to <strong>100% of the service cost</strong>.</li>
          </ul>
        </div>
      )
    },
    {
      question: "How far in advance should I book my travel consultation?",
      icon: <Clock className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>We recommend booking your consultation <strong>at least 2 weeks or more before travel</strong>.</p>
          <p>This allows sufficient time to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Schedule pharmacy appointments</li>
            <li>Ensure vaccines such as yellow fever meet the 10-day requirement for optimal protection and International Certificate of Vaccination or Prophylaxis (ICVP) validity for airport and border authorities</li>
          </ul>
          <p className="text-cyan-600 dark:text-cyan-400 font-medium">Earlier booking is strongly encouraged for complex itineraries or multi-dose vaccines.</p>
        </div>
      )
    },
    {
      question: "How will I receive my vaccines?",
      icon: <Syringe className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>After your virtual consultation, the pharmacist will coordinate and book your vaccination appointment with one of our partner pharmacies or clinics closest to your residence.</p>
          <p>Where required, you will also receive the <strong>International Certificate of Vaccination or Prophylaxis (ICVP)</strong> as proof of vaccination. This may be required for vaccines such as:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Yellow fever</li>
            <li>Meningococcal vaccine</li>
            <li>Polio</li>
          </ul>
        </div>
      )
    },
    {
      question: "Can prescriptions be provided during the consult?",
      icon: <FileText className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p><strong>Yes.</strong> In collaboration with a licensed physician, travel prescriptions may be offered when clinically appropriate.</p>
          <p>This may include:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Malaria prophylaxis</li>
            <li>Traveler's diarrhea treatment</li>
            <li>Altitude illness prevention</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-400">All prescriptions are reviewed and authorized through the collaborating physician and sent to a pharmacy convenient to you.</p>
        </div>
      )
    },
    {
      question: "Who is Voyage Vax?",
      icon: <HelpCircle className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p><strong>Voyage Vax</strong> is a partner travel health clinic that collaborates with Haven Travel Health to support vaccination services and clinical workflow.</p>
          <p>Haven Travel Health and Voyage Vax share the Jane App platform to securely manage bookings, documentation, and coordination of care. This allows for seamless scheduling and record-keeping while maintaining patient privacy and regulatory compliance.</p>
          <p className="text-cyan-600 dark:text-cyan-400 font-medium">Haven Travel Health and Voyage Vax remain independent but collaborating clinics.</p>
        </div>
      )
    },
    {
      question: "Is my personal and medical information secure?",
      icon: <Shield className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p><strong>Yes.</strong> All patient information is securely stored within <strong>Jane App</strong>, a Canadian health-care–compliant platform.</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Access is restricted to authorized health-care providers only</li>
            <li>Information is handled in accordance with applicable provincial privacy legislation</li>
          </ul>
        </div>
      )
    },
    {
      question: "Who provides my care?",
      icon: <Users className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p>Your consultation is conducted by a <strong>licensed pharmacist</strong> in good standing with the College of Pharmacists of British Columbia, working in collaboration with a <strong>licensed physician</strong>.</p>
          <p>Vaccinations are administered through our partner pharmacies, depending on location and availability.</p>
        </div>
      )
    },
    {
      question: "Do you offer evening or weekend appointments for virtual consults?",
      icon: <Calendar className="w-5 h-5" />,
      answer: (
        <div className="space-y-3">
          <p><strong>Yes.</strong> We offer weekday, evening, and weekend virtual appointments to accommodate busy schedules.</p>
          <p className="text-amber-600 dark:text-amber-400 font-medium">Vaccine appointments and pharmacy hours, however, are only available on weekdays.</p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-navy-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 pt-24 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
              Everything you need to know about booking, payments, vaccinations, and our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="py-16 -mt-16 relative z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-white/10">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`border-b border-gray-200 dark:border-white/10 last:border-b-0 ${
                  openIndex === index ? 'bg-cyan-50/50 dark:bg-cyan-900/10' : ''
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-6 flex items-start gap-4 text-left hover:bg-gray-50 dark:hover:bg-navy-700/50 transition-colors"
                >
                  <div className={`p-2 rounded-lg shrink-0 transition-colors ${
                    openIndex === index 
                      ? 'bg-cyan-500 text-white' 
                      : 'bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {faq.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-lg font-bold transition-colors ${
                      openIndex === index 
                        ? 'text-cyan-600 dark:text-cyan-400' 
                        : 'text-navy-900 dark:text-white'
                    }`}>
                      {faq.question}
                    </h3>
                  </div>
                  
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180 text-cyan-600 dark:text-cyan-400' : ''
                    }`}
                  />
                </button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 sm:px-8 pb-6 pl-16 sm:pl-20 text-gray-700 dark:text-gray-300 leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Our team is here to help. Reach out and we'll get back to you as soon as possible.
          </p>
          <a
            href="mailto:saba@haventravelhealth.ca"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
